import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types/user'
import { api } from '@/lib/api'
import { setToken as setInMemoryToken } from '@/lib/tokenHolder'
import { registerSessionHandlers } from '@/lib/authSession'
import { disconnectCable } from '@/lib/actionCable'
// Spec 10, R4: o bloqueio por assinatura e um estado de sessao — some junto com ela.
import { clearSubscriptionBlock } from '@/lib/subscriptionBlock'
// TRANSC (D9): o cache de consultas (transcricoes inclusive) morre com a sessao.
import { clearSessionData } from '@/lib/sessionData'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  setUser: (user: User | null, token?: string | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  getToken: () => string | null
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      setUser: (user, token = null) => {
        const newToken = token ?? get().token
        // Update in-memory token immediately for API calls
        setInMemoryToken(newToken)
        set({
          user,
          token: newToken,
          isAuthenticated: !!user,
          isLoading: false,
          error: null,
        })
      },

      getToken: () => get().token,

      setLoading: (loading) => {
        set({ isLoading: loading })
      },

      setError: (error) => {
        set({ error })
      },

      logout: async () => {
        try {
          await api.auth.logout()
        } catch {
          // Ignore logout errors
        } finally {
          // Clear in-memory token
          setInMemoryToken(null)
          // Sem isto o proximo login herdaria o bloqueio do usuario anterior.
          clearSubscriptionBlock()
          void clearSessionData()
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: null,
          })
        }
      },

      checkAuth: async () => {
        set({ isLoading: true })
        try {
          const response = await api.auth.me()
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },
    }),
    {
      name: 'medhunter-auth',
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Sync the in-memory token when store is rehydrated from localStorage
        if (state?.token) {
          setInMemoryToken(state.token)
        }
      },
    }
  )
)

// O ApiClient renova o access token sozinho (lib/authSession.ts); o store so guarda o
// token novo e, quando a renovacao falha de vez, encerra a sessao — os layouts
// (`DashboardLayout`, `AdminLayout`...) veem `isAuthenticated` cair e mandam para /login.
registerSessionHandlers({
  onTokenRefreshed: (token) => {
    useAuthStore.setState({ token })
  },
  onSessionExpired: () => {
    disconnectCable()
    clearSubscriptionBlock()
    void clearSessionData()
    useAuthStore.setState({ user: null, token: null, isAuthenticated: false, isLoading: false })
  },
})
